import { CloseIcon, EmptyBoxImage } from '@approvalmax/ui';
import { Box, Button, Flex, NoContent, Table } from '@approvalmax/ui/src/components';
import { mapReference } from 'modules/data/domain/schemas';
import { memo, useMemo } from 'react';

import { useNetSuiteCustomFields, useNewLineCustomFieldsDefinitions } from '../../../../NetSuiteRequestCard.hooks';
import { Footer } from './components';
import { getColumnDefinitions } from './Content.config';
import { messages } from './Content.messages';
import { ContentProps, NetSuiteMatchingLine } from './Content.types';

export const Content = memo((props: ContentProps) => {
    const { request, lineType, lineNumber, matchingInfo, isFetching, onDropdownClose } = props;

    const { matchingLinesInfos } = matchingInfo;

    const { data: companyCustomFields } = useNetSuiteCustomFields(request.companyId);

    const lines = useMemo(
        () =>
            matchingLinesInfos.reduce<NetSuiteMatchingLine[]>((matchedLines, { line, ...info }) => {
                if (line && info.documentLineType === lineType && info.documentLineNumber === lineNumber) {
                    matchedLines.push({
                        ...line,
                        id: `line-${line.lineNumber}`,
                        name: info.friendlyName,
                        date: info.date,
                        totalAmount: info.totalAmount,
                        requestId: info.requestId,
                        taxCode: mapReference(line.taxCode),
                        department: mapReference(line.department),
                        class: mapReference(line.class),
                        location: mapReference(line.location),
                        customer: mapReference(line.customer),
                        category: mapReference(line.category),
                        account: mapReference(line.account),
                        item: mapReference(line.item),
                    });
                }

                return matchedLines;
            }, []),
        [matchingLinesInfos, lineType, lineNumber]
    );

    const customFieldsColumnDefinition = useNewLineCustomFieldsDefinitions(lines, companyCustomFields?.Fields ?? []);

    const columnDefinitions = useMemo(
        () => [
            ...getColumnDefinitions({
                request,
                lineType,
            }),
            ...customFieldsColumnDefinition,
        ],
        [request, lineType, customFieldsColumnDefinition]
    );

    return (
        <Box spacing='24'>
            <Flex justifyContent='end'>
                <Button icon noPadding onClick={onDropdownClose} variant='text' color='blue100'>
                    <CloseIcon size={16} color='midnight80' />
                </Button>
            </Flex>

            {isFetching || lines.length ? (
                <Flex direction='column' spacing='16'>
                    <Box width='100%' overflowX='auto' overflowY='auto'>
                        <Table
                            hoverable={false}
                            headerColor='transparent'
                            columns={columnDefinitions}
                            data={lines}
                            hideEmptyColumns
                        />
                    </Box>

                    <Footer request={request} matchingLines={matchingLinesInfos} />
                </Flex>
            ) : (
                <Flex justifyContent='center'>
                    <NoContent icon={<EmptyBoxImage />} heading={messages.emptyMatching} />
                </Flex>
            )}
        </Box>
    );
});

Content.displayName = 'Content';
